import React, { useContext } from 'react';
import { Form, Formik } from 'formik';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';
import * as Yup from 'yup';
import { useNotificationQueue } from 'components/notification';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import {
  MUTATION_ADD_PERSON_INJURY,
  MUTATION_EDIT_PERSON_INJURY,
  QUERY_GET_PERSON_MEDICALS
} from 'services/aws/injury-query';
import { StoreContext } from 'index';
import { useMedicalContext } from 'contexts/MedicalContext';
import messages from 'messages';
import { parseDateString } from 'utils/date';
import { format } from 'date-fns';
import { PersonInjuryType } from 'constants.js';
import { ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL } from 'routes/RouteList';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';

function IssueForm({ personIssue, extraClassNames, onComplete, children }) {
  const notification = useNotificationQueue();
  const intl = useIntl();
  const { push } = useHistory();

  const { entityId, groupId } = useParams();
  const {
    authStore: {
      user: { rootEntityId }
    }
  } = useContext(StoreContext);

  const person = useEntityContext(s => s.person);
  const { medical } = useMedicalContext();

  const [addIssue] = useMutation(MUTATION_ADD_PERSON_INJURY);

  const [editIssue] = useMutation(MUTATION_EDIT_PERSON_INJURY);

  const onSubmitHandler = async personIssue => {
    const issueObject = {
      title: personIssue.title,
      medicalId: medical.id,
      injuryDate: format(personIssue.injuryDate, 'yyyy-LL-dd'),
      injuryEndDate: personIssue.injuryEndDate
        ? format(personIssue.injuryEndDate, 'yyyy-LL-dd')
        : null,
      personInjuryType: PersonInjuryType.ISSUE,
      meta: JSON.stringify({
        description: personIssue.description,
        extraInfo: personIssue.extraInfo
      })
    };

    if (personIssue.id) {
      await editIssue({
        variables: { ...personIssue, ...issueObject },
        refetchQueries: [
          {
            query: QUERY_GET_PERSON_MEDICALS,
            variables: { entityId: rootEntityId, personId: person.id }
          }
        ]
      }).then(res => {
        notification.add(res.data.editPersonInjury.id, {
          message: intl.formatMessage(messages.rehabIssueSaved)
        });
        if (onComplete) onComplete();
        push(
          generatePath(ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL, {
            entityId,
            groupId,
            personId: person.id,
            medicalId: medical.id,
            issueId: res.data.editPersonInjury.id
          })
        );
      });
    } else {
      await addIssue({
        variables: issueObject,
        refetchQueries: [
          {
            query: QUERY_GET_PERSON_MEDICALS,
            variables: { entityId: rootEntityId, personId: person.id }
          }
        ]
      }).then(res => {
        notification.add(res.data.addPersonInjury.id, {
          message: intl.formatMessage(messages.rehabIssueSaved)
        });
        if (onComplete) onComplete();
        push(
          generatePath(ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL, {
            entityId,
            groupId,
            personId: person.id,
            medicalId: medical.id,
            issueId: res.data.addPersonInjury.id
          })
        );
      });
    }
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(intl.formatMessage(messages.titleRequired)),
    injuryDate: Yup.date()
      .transform(parseDateString)
      .typeError(intl.formatMessage(messages.dateRequired))
      .required(intl.formatMessage(messages.dateRequired)),
    injuryEndDate: Yup.date()
      .transform(parseDateString)
      .nullable()
      .default(null)
  });

  return (
    <Formik
      initialValues={personIssue}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {props => {
        return (
          <Form className={classNames(extraClassNames)} noValidate>
            {typeof children === 'function' ? children(props) : children}
          </Form>
        );
      }}
    </Formik>
  );
}

export default IssueForm;
